import * as CL from '@design-system/component-library';
import { Modal } from '../Modal/Modal';
import { StickyFooter } from '../StickyFooter';
import { SupportedModelCategories } from '../../common/utils/catalogUtils';
import { cancelMsg, nextMsg, pcsMsg, returnMsg, t } from '../../common/i18n';
import { dsClass } from '../../common/constants/dsClasses';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { getProductCategoryDetails, getSelectedCatalogProducts } from './CatalogProductSelectionUtils';
import { paths } from '../../common/constants/pathVariables';
import { useFormContext } from 'react-hook-form';
import { useSearchParams } from '../../common/hooks/useSearchParams';
import { useState } from 'react';
import type { CatalogProduct, SupportedModelCategory } from '../../common/utils/catalogUtils';
import type { DeviceSelection } from './CatalogProductSelectionFormWrapper';

interface CatalogProductSelectionFooterProps {
  catalogProducts: Record<string, CatalogProduct[]>;
}

export interface CategorySummaryProps {
  selectedProducts: Record<SupportedModelCategory, CatalogProduct[]>;
}

interface ConfirmationModalProps {
  isVisible: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const ProductSelectionConfirmationModal = ({ isVisible, onClose, onConfirm }: ConfirmationModalProps) =>
  isVisible ? (
    <Modal heading={t.AZKN('Are you sure you want to cancel?')} onClose={onClose} closeable={true} size="s">
      <div className="of-catalog-products-selection__confirmation-modal-content">
        <p>{t.BFQ0('Your changes will not be saved.')}</p>
        <div className="of-catalog-products-selection__confirmation-modal-buttons">
          <CL.Button color="link" onClick={onClose}>
            {t.V9XM(`Don't cancel`)}
          </CL.Button>
          <CL.Button color="primary" onClick={onConfirm}>
            {t.B2V1(cancelMsg)}
          </CL.Button>
        </div>
      </div>
    </Modal>
  ) : null;

const CategorySummary = ({ selectedProducts }: CategorySummaryProps) => (
  <div className={dsClass.TEXT_S}>
    {SupportedModelCategories.map(cat => {
      return selectedProducts[cat]
        ? `${getProductCategoryDetails(cat).displayText}: ${selectedProducts[cat].length} ${t.B3MG(pcsMsg)}`
        : null;
    })
      .filter(line => line)
      .join(', ')}
  </div>
);

export const CatalogProductSelectionFooter = ({ catalogProducts }: CatalogProductSelectionFooterProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { mdmId } = useSearchParams<{ mdmId: string }>();
  const { getValues } = useFormContext<DeviceSelection>();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const selectedProductCodes = getValues('selectedProductCodes') || [];

  const selectedCatalogProducts = getSelectedCatalogProducts(catalogProducts, selectedProductCodes);

  const isExistingCatalog = location.state?.virtualCatalog;
  // If catalog has virtualcatalog it is not a 'new' catalog then navigate to the catalog info page, otherwise to catalog creation page
  const navigateBack = () =>
    isExistingCatalog
      ? navigate(
          `${generatePath(paths.COMPANY_INFO_CATALOG, { catalogCode: location.state.virtualCatalog.virtualCatalogCode })}?mdmId=${location.state.mdmId}`
        )
      : navigate(`${paths.COMPANY_INFO_CATALOG_LIST_NEW}?mdmId=${mdmId}`, {
          state: { catalog: location.state?.catalog || {}, newCatalog: true },
        });

  return (
    <div className="of-catalog-products-selection__footer">
      <ProductSelectionConfirmationModal
        isVisible={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={() => {
          setShowConfirmationModal(false);
          navigateBack();
        }}
      />

      <StickyFooter active={true}>
        <CL.Grid>
          <CL.GridRow justifyCenter className="of-catalog-products-selection__footer">
            <CL.GridCol colWidthXS={4} colWidthS={3} colWidthL={5}>
              <div>
                <div>
                  {t.Z875('Selected in total')}:{' '}
                  <b>
                    {selectedProductCodes.length} {t.B3MG(pcsMsg)}
                  </b>
                </div>
                <CategorySummary selectedProducts={selectedCatalogProducts} />
              </div>
            </CL.GridCol>
            <CL.GridCol
              colWidthXS={4}
              colWidthS={3}
              colWidthL={5}
              className={`${dsClass.TEXT_ALIGN_RIGHT} ${dsClass.PADDING_BOTTOM_2} ${dsClass.DISPLAY_FLEX} ${dsClass.JUSTIFY_CONTENT_FLEX_END} of-catalog-products-selection__footer__buttons`}
            >
              <CL.Button
                color="link"
                size="l"
                type="button"
                onClick={() => setShowConfirmationModal(true)}
                className="of-catalog-products-selection__cancel-button"
              >
                {isExistingCatalog ? t.B2V1(cancelMsg) : t.D1E9(returnMsg)}
              </CL.Button>
              <CL.Button color="linkblue" size="l" type="submit">
                {t.F0MY(nextMsg)}
              </CL.Button>
            </CL.GridCol>
          </CL.GridRow>
        </CL.Grid>
      </StickyFooter>
    </div>
  );
};
